/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getSimpleProductsAction, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { IInvoiceDetailsViewProps } from '@msdyn365-commerce-modules/invoice-details/src/modules/invoice-details/./invoice-details';
import { IInvoiceDetailsProductsData, InvoiceDetailsProductsComponent } from './custom-components/invoice-details-products';
// import { InvoiceDetailsModel } from './custom-components/invoice-data';
import { CommerceProperty, SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { InvoicePaidStatus, OrderInvoice, SalesInvoice } from '@msdyn365-commerce/retail-proxy';
import { IPayableInvoice } from '@msdyn365-commerce-modules/invoice';
import { IInvoiceDetailsProps } from '../definition-extensions/invoice-details.ext.props.autogenerated';

import InvoiceDetailsPaymentSummary from './custom-components/invoice-details-payment-summary';

import { InvoiceDetailsHeaderComponent } from './custom-components/invoice-details-header';

/**
 * TODO:These interfaces and class will be in a seprate file, due to time limitation we are keeping them here.
 */
export interface IInvoicePaymentDetails {
    totalDiscount?: number;
    subtotal?: number;
    totalCharges?: number;
    totalTax?: number;
    totalAmount?: number;
    currencyCode?: string;
}

export interface IInvoiceProductLine extends SalesInvoiceLine {
    primaryImageUrl?: string;
}

class InvoiceDetailsModel implements IPayableInvoice, IInvoicePaymentDetails {
    public id: string;

    public invoiceDate?: Date;

    public customerAccountNumber?: string;

    public customerName?: string;

    public dueDate?: Date;

    public amountDue?: number;

    public status?: InvoicePaidStatus;

    public lines: IInvoiceProductLine[];

    public totalDiscount?: number;

    public subtotal?: number;

    public totalCharges?: number;

    public totalTax?: number;

    public totalAmount?: number;

    public currencyCode?: string;

    public extensionProperties?: CommerceProperty[];

    public constructor(invoice: OrderInvoice, invoiceDetails: SalesInvoice, products: SimpleProduct[]) {
        this.id = invoice.Id || '';
        this.invoiceDate = invoice.InvoiceDate ? new Date(invoice.InvoiceDate) : undefined;
        this.customerName = invoice.CustomerName;
        this.dueDate = invoice.InvoiceDueDate ? new Date(invoice.InvoiceDueDate) : undefined;
        this.amountDue = invoice.AmountBalance;
        this.status = invoice.InvoicePaidStatusValue;
        this.lines = (invoiceDetails.SalesInvoiceLine || []).map(line => {
            const productLine: IInvoiceProductLine = {
                ...line,
                primaryImageUrl: products.find(product => product.RecordId === line.RecordId)?.PrimaryImageUrl
            };
            return productLine;
        });

        this.totalDiscount = invoiceDetails.TotalDiscount;
        this.subtotal = invoiceDetails.InvoiceSubtotal;
        this.totalCharges = invoiceDetails.TotalCharges;
        this.totalTax = invoiceDetails.TotalTaxAmount;
        this.totalAmount = invoice.TotalAmount;
        this.currencyCode = invoice.CurrencyCode;
        this.extensionProperties = invoiceDetails.ExtensionProperties;
    }

    public get canBePaid(): boolean {
        return this.status != InvoicePaidStatus.Paid;
    }
}

export const InvoiceDetailsView: React.FC<IInvoiceDetailsViewProps & IInvoiceDetailsProps<{}>> = props => {
    const [invoiceDetailsState, setInvoiceDetailsState] = React.useState<IInvoiceDetailsProductsData>();
    const {
        header,
        payInvoiceError,
        resources: { invoiceSubTitle }
    } = props;
    const _className: string = 'msc-invoice-details';
    const invoiceDetails = props.data.invoiceDetails?.result!;
    const { imageSettings } = props.config;
    async function _getProducts(invoiceLines: SalesInvoiceLine[] = [], channelId?: number): Promise<SimpleProduct[]> {
        const productInputs = invoiceLines
            .filter(product => product.RecordId)
            .map(product => new ProductInput(product.RecordId!, props.context.request.apiSettings, channelId));
        return getSimpleProductsAction(productInputs, props.context.actionContext);
    }
    React.useEffect(() => {
        _getProducts(invoiceDetails.SalesInvoiceLine, invoiceDetails.ChannelId)
            .then(value => {
                const invoiceDetailModel = new InvoiceDetailsModel(props.data.invoice.result!, invoiceDetails, value);
                setInvoiceDetailsState({
                    invoiceDetails: invoiceDetailModel
                });
            })
            .catch(error => {
                props.context.telemetry.error(`${error}`);
            });
    }, []);
    // const checkLoader = invoiceDetailsState?.invoiceDetails.lines.length! > 0 ? true : false;
    const _renderHeader = (): JSX.Element => {
        return (
            <InvoiceDetailsHeaderComponent
                className={header.props.className}
                id={`${header.props.className}__${invoiceDetailsState?.invoiceDetails.id}`}
                data={{ invoiceDetails: invoiceDetailsState?.invoiceDetails! }}
                typeName={props.typeName}
                context={props.context}
                resources={props.resources}
                onError={header.props.onError}
            />
        );
    };
    const _renderPaymentDetails = (): JSX.Element => {
        // const invoiceDetails = this.state.invoiceDetails!;
        return (
            <InvoiceDetailsPaymentSummary
                className={`msc-invoice-details__payment-details`}
                id={`msc-invoice-details__payment-details__${invoiceDetailsState?.invoiceDetails.id}`}
                data={{ invoicePaymentDetails: invoiceDetailsState?.invoiceDetails! }}
                typeName={props.typeName}
                context={props.context}
                resources={props.resources}
            />
        );
    };

    return (
        <Module {...props.moduleProps}>
            {invoiceDetailsState?.invoiceDetails.lines && _renderHeader()}
            {invoiceDetailsState?.invoiceDetails.lines && _renderPaymentDetails()}
            <h2 className='sub-title'>{invoiceSubTitle}</h2>
            {invoiceDetailsState?.invoiceDetails.lines && (
                <InvoiceDetailsProductsComponent
                    className={`${_className}__products`}
                    id={`${_className}__${invoiceDetailsState?.invoiceDetails.id}`}
                    data={{ invoiceDetails: invoiceDetailsState?.invoiceDetails }}
                    typeName={props.typeName}
                    context={props.context}
                    resources={props.resources}
                    imageSettings={imageSettings}
                />
            )}
            {payInvoiceError}
            {/* {checkLoader ? null : (
                <div className='spinner-wrap'>
                    <div className='spinner-border' role='status'>
                        <span className='sr-only'>{props.resources.loadingLabel}</span>
                    </div>
                    <p>Loading...</p>
                </div>
            )} */}
        </Module>
    );
};

export default InvoiceDetailsView;
